/**
 *****************************************
 *                                       *
 * NAGARRO  - CSX - CONFIDENTIAL         *
 *                                       *
 *****************************************
 */

import { mergeMap } from 'rxjs/operators';

import { AppUtilityService } from "../../core/services/app-utility.service";
import { BehaviorSubject ,  Observable } from "rxjs";
import { ConfigService } from "../../core/services/config.service";
import { DataStoreService } from "../../core/services/datastore.service";
import { HttpStatus } from "../../core/model/http-status-enum.model";
import { Injectable } from "@angular/core";
import { LmsLoaderService } from "./../../core/services/lms-loader.service";
import { LmsLoggerService } from "../../core/services/lms-logger.service";
import { LocoNote } from "./../model/loco-note.model";
import { MessageDetails } from "../../core/model/message-details.model";
import { MessageHandlerService } from "../../core/services/message-handler.service";
import { NotesDetails } from "./../model/notes-details.model";
import { PopupService } from "../../core/services/popup.service";
import { Router } from "@angular/router";
import { Tab } from "../../core/model/tab.model";
import { TabData } from "../../core/model/tab-data.model";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { tap } from "rxjs/operators";

/**
 * Loco Data Service to get data for locomotives details.
 * @export
 * @class LocoDataService
 */
@Injectable()
export class NotesDataService {
  loadData: Observable<NotesDetails>;
  private loadDataBS: BehaviorSubject<NotesDetails>;
  private RESOURCE_URL = "/notes";
  private configData: any;
  private apiUrl: string;

  /**
   * Creates an instance of LocoDataService.
   * @param {HttpClient} http
   * @param {ConfigService} configService
   * @param {DataStoreService} dataStoreService
   * @param {LmsLoggerService} lmsLoggerService
   * @memberOf LocoDataService
   */
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private dataStoreService: DataStoreService,
    private lmsLoggerService: LmsLoggerService,
    private router: Router,
    private lmsLoaderService: LmsLoaderService,
    private popupService: PopupService,
    private messageHandlerService: MessageHandlerService,
    private appUtilityService: AppUtilityService
  ) {
    this.loadDataBS = <BehaviorSubject<NotesDetails>>new BehaviorSubject({});
    this.loadData = this.loadDataBS.asObservable();
    this.configData = this.configService.getConfigData();
    this.apiUrl = this.configData.urls.API_URL;
  }

  /**
   * Fetching Data from Rest Service Provided or loading data from dataStoreService if the data is already present.
   * @param {Tab} tab
   * @param {boolean} isRefresh
   * @memberOf LocoDataService
   */
  load(
    tab: Tab,
    win: string,
    nt: string,
    fromOtherOperation?: boolean,
    messageText?: string
  ) {
    let locoData: NotesDetails;
    let foundInDataStore = false;
    setTimeout(() => {
      if (document.getElementById("autoCompleteTexttopHeader")) {
        const selectBox = <HTMLInputElement>(
          document.getElementById("autoCompleteTexttopHeader")
        );
        if (selectBox) {
          selectBox.select();
        }
      }
    }, 50);
    if (tab && tab.tabSearchParameter) {
      if (tab.tabSearchParameter.searchText) {
        tab.tabSearchParameter.searchText =
          tab.tabSearchParameter.searchText.trim();
      } else {
        this.lmsLoaderService.hideLoader();
        return;
      }
      if ("error" === tab.subTypeDetail[tab.subType.toLowerCase()].status) {
        this.lmsLoaderService.hideLoader();
        return;
      }
      if (
        "LWLD" === win &&
        !this.appUtilityService.isValidLocomotiveId(
          tab.tabSearchParameter.searchText
        )
      ) {
        this.invalidLocoID();
      } else if (
        "LWTD" === win &&
        !this.appUtilityService.isValidTrainId(
          tab.tabSearchParameter.searchText
        )
      ) {
        this.invalidTrainID();
      } else if (
        "LWTW" === win &&
        !this.appUtilityService.isMnemonicExist(
          tab.tabSearchParameter.searchText
        )
      ) {
        this.invalidTerminalID();
      } else {
        if (tab.subTypeDetail[tab.subType.toLowerCase()].status !== "refresh") {
          const tabData = this.dataStoreService.getValue(tab);
          if (tabData) {
            locoData = <NotesDetails>tabData.data[tab.subType];
            foundInDataStore = true;
            this.loadDataBS.next(locoData);
            this.lmsLoaderService.hideLoader();
          }
        } else {
          foundInDataStore = false;
        }
        if (!foundInDataStore) {
          this.notesDataSearch(win, tab, fromOtherOperation, messageText, nt);
        }
      }
    } else {
      this.lmsLoaderService.hideLoader();
      this.loadDataBS.next(null);
    }
  }

  /**
   * @memberof NotesDataService
   */
  invalidTrainID() {
    const lLocoDetail = <NotesDetails>{
      httpStatus: 400,
      errorMessage: this.configService.getConfigData().messages.invalidTrain,
    };
    this.loadDataBS.next(lLocoDetail);
    this.lmsLoaderService.hideLoader();
    this.lmsLoggerService.logError(
      "NotesDataService => load()",
      this.configService.getConfigData().messages.invalidTrain
    );
  }

  /**
   * @memberof NotesDataService
   */
  invalidLocoID() {
    const lLocoDetail = <NotesDetails>{
      httpStatus: 400,
      errorMessage:
        this.configService.getConfigData().messages.invalidLocomotive,
    };
    this.loadDataBS.next(lLocoDetail);
    this.lmsLoaderService.hideLoader();
    this.lmsLoggerService.logError(
      "NotesDataService => load()",
      this.configService.getConfigData().messages.invalidLocomotive
    );
  }

  /**
   * @memberof NotesDataService
   */
  invalidTerminalID() {
    const lLocoDetail = <NotesDetails>{
      httpStatus: 400,
      errorMessage: this.configService.getConfigData().messages.invalidTerminal,
    };
    this.loadDataBS.next(lLocoDetail);
    this.lmsLoaderService.hideLoader();
    this.lmsLoggerService.logError(
      "NotesDataService => load()",
      this.configService.getConfigData().messages.invalidTerminal
    );
  }

  /**
   * @param {any} win
   * @param {any} id
   * @param {Tab} tab
   * @param {any} fromOtherOperation
   * @param {any} messageText
   * @memberof NotesDataService
   */
  notesDataSearch(win, tab: Tab, fromOtherOperation, messageText, nt) {
    let id = tab.tabSearchParameter.searchText;
    this.lmsLoaderService.showLoader();
    const opr = "INQU";
    if ("LWLD" === win) {
      id = this.appUtilityService.formatLocomotiveId(id);
    }
    this.lmsLoggerService.logInfo("NotesDataService=>load service call starts");
    this.http
      .get(
        `${this.configService.getConfigData().urls.API_URL}${
          this.RESOURCE_URL
        }?id=${id}&opr=${opr}&wc=${win}&nt=${nt}`
      )
      .pipe(
        tap(() =>
          this.lmsLoggerService.logInfo(
            "NotesDataService=>load service call ends"
          )
        )
      )
      .subscribe(
        (data) => {
          this.subscribeData(data, tab, fromOtherOperation, messageText);
        },
        (error) => {
          this.errorLoadData(error, fromOtherOperation, messageText);
        }
      );
  }

  /**
   * @param {*} error
   * @param {boolean} fromOtherOperation
   * @param {string} messageText
   * @memberof NotesDataService
   */
  errorLoadData(error: any, fromOtherOperation: boolean, messageText: string) {
    this.lmsLoaderService.hideLoader();
    const lLocoDetail = <NotesDetails>{
      httpStatus: error.status,
      errorMessage: fromOtherOperation
        ? messageText + ", but an error has occurred while refreshing the page."
        : this.appUtilityService.getHttpErroMessage(error, false),
    };
    this.loadDataBS.next(lLocoDetail);
    this.lmsLoggerService.logError(
      "NoteDataService => load()",
      this.appUtilityService.getHttpErroMessage(error, false)
    );
  }
  /**
   * @param {*} data
   * @param {Tab} tab
   * @param {boolean} fromOtherOperation
   * @param {string} messageText
   * @memberOf NotesDataService
   */
  subscribeData(
    data: any,
    tab: Tab,
    fromOtherOperation: boolean,
    messageText: string
  ) {
    if (data && data.priorityCode) {
      const pCode = parseInt(data.priorityCode, 10);
      if (pCode >= 1 && pCode <= 4) {
        const notesDetails = <NotesDetails>data;
        const notesList = <LocoNote[]>data.listOfNotes;
        const tabData = <TabData>{
          id: tab.id,
          subType: tab.subType,
        };
        tabData.data = tabData.data ? tabData.data : {};
        tabData.data[tab.subType] = notesDetails;
        notesDetails.refreshTime = data.refreshTime;
        notesDetails.httpStatus = HttpStatus.OK;
        notesDetails.tabId = tab.id;
        tab.subTypeDetail.notes.message = "";
        notesDetails.errorMessage = "";
        this.lmsLoaderService.hideLoader();
        this.dataStoreService.putValue(tab, tabData);
        this.loadDataBS.next(notesDetails);
        this.lmsLoggerService.logInfo(
          "NoteDataService => load()",
          `Note Data Fetch Successfull for ${JSON.stringify(tab)}`
        );
        if (fromOtherOperation) {
          this.messageHandlerService.handleMessage(<MessageDetails>{
            type: this.configData.message.type.client,
            level: this.configData.message.level.success,
            target: this.configData.message.target.page,
            message: messageText,
          });
        }
      } else {
        this.lmsLoaderService.hideLoader();
        const lLocoDetail = <NotesDetails>{
          httpStatus: 400,
          errorMessage: fromOtherOperation
            ? messageText + ", but " + data.messageText
            : data.messageText,
        };
        this.loadDataBS.next(lLocoDetail);
        this.lmsLoggerService.logError(
          "NoteDataService => load()",
          data.messageText
        );
      }
    }
  }

  /**
   * @param {Tab} tab
   * @param {*} locoNotesList
   * @param {string} win
   * @param {string} nt
   * @returns {Observable<Response>}
   * @memberOf NotesDataService
   */
  updateNote(
    tab: Tab,
    locoNotesList: any,
    win: string,
    nt: string
  ): Observable<Response> {
    const opr = "NOTU";
    let id = tab.tabSearchParameter.searchText;
    if ("LWLD" === win) {
      id = this.appUtilityService.formatLocomotiveId(id);
    }
    let headers = new HttpHeaders();
    headers = headers.append("Content-Type", "application/json");
    const options = { headers: headers };
    this.lmsLoggerService.logInfo(
      "NotesDataService=>updateNote service call starts"
    );
    return this.http
      .put<any>(
        `${this.apiUrl}${this.RESOURCE_URL}?opr=${opr}&wc=${win}&id=${id}&nt=${nt}`,
        JSON.stringify(locoNotesList),
        options
      ).pipe(
        tap(() => this.lmsLoggerService.logInfo("NotesDataService=>updateNote service call ends"))
      )
  }

  /**
   * @memberOf LocoDataService
   */
  cleanBehaviourSubject() {
    this.loadDataBS.next(null);
  }

  /**
   * @param {LocoNote} locoNote
   * @param {string} win
   * @param {string} nt
   * @param {Tab} tab
   * @returns {Observable<Response>}
   * @memberOf NotesDataService
   */
  addNote(
    locoNote: LocoNote,
    win: string,
    nt: string,
    tab: Tab
  ): Observable<Response> {
    const opr = "NOTA";
    let id = tab.tabSearchParameter.searchText;
    if ("LWLD" === win) {
      id = this.appUtilityService.formatLocomotiveId(id);
    }
    let headers = new HttpHeaders();
    headers = headers.append("Content-Type", "application/json");
    const options = { headers: headers };
    this.lmsLoggerService.logInfo(
      "NotesDataService=>addNote service call starts"
    );
    return this.http
      .post<any>(
        `${this.apiUrl}${this.RESOURCE_URL}?opr=${opr}&wc=${win}&id=${id}&nt=${nt}`,
        JSON.stringify(locoNote),
        options
      )
      .pipe(
        tap(() => this.lmsLoggerService.logInfo("NotesDataService=>addNote service call ends"))
      )
  }

  /**
   * Delete Loco Service Call
   * @param {string} currentAssignment
   * @param {string} locoId
   * @returns {Observable<Response>}
   * @memberOf LocoDataService
   */
  deleteNote(
    locoNotesList: any,
    win: string,
    nt: string,
    tab: Tab
  ): Observable<Response> {
    const opr = "NOTD";
    let id = tab.tabSearchParameter.searchText;
    if ("LWLD" === win) {
      id = this.appUtilityService.formatLocomotiveId(id);
    }
    let headers = new HttpHeaders();
    headers = headers.append("Content-Type", "application/json");
    const options = { headers: headers };
    this.lmsLoggerService.logInfo(
      "NotesDataService=>deleteNote service call starts"
    );
    return this.http
      .put<any>(
        `${this.apiUrl}${this.RESOURCE_URL}/delete?opr=${opr}&wc=${win}&id=${id}&nt=${nt}`,
        JSON.stringify(locoNotesList),
        options
      )
      .pipe(
        tap(() => this.lmsLoggerService.logInfo("NotesDataService=>deleteNote service call ends"))
      );
  }

  /**
   * @param {string} win
   * @param {string} nt
   * @param {Tab} tab
   * @returns {Observable<Response>}
   * @memberOf NotesDataService
   */
  deleteAllNote(win: string, nt: string, tab: Tab): Observable<any> {
    const opr = "NOTE";
    let id = tab.tabSearchParameter.searchText;
    if ("LWLD" === win) {
      id = this.appUtilityService.formatLocomotiveId(id);
    }
    this.lmsLoggerService.logInfo(
      "NotesDataService=>deleteAllNote service call starts"
    );
    return this.http
      .delete<any>(
        `${this.apiUrl}${this.RESOURCE_URL}?opr=${opr}&wc=${win}&id=${id}&nt=${nt}`
      )
      .pipe(
        tap(() => this.lmsLoggerService.logInfo("NotesDataService=>deleteAllNote service call ends"))
      );
  }
}
